import React, { Component } from "react";
import {
  Table,
  Message,
  Label,
  Segment,
  Header,
  Icon,
} from "semantic-ui-react";
import { loanApi } from "@api/loans";
import { fineApi } from "@api/fine/fine";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { calculateBusinessDaysLuxon } from "@utils/fineUtils";

class UnreturnedMaterialsTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      unreturnedMaterials: [],
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchUnreturnedMaterials();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.fromDate !== this.props.fromDate ||
      prevProps.toDate !== this.props.toDate
    ) {
      this.fetchUnreturnedMaterials();
    }
  }

  fetchUnreturnedMaterials = async () => {
    this.setState({ loading: true, error: null });

    try {
      let loansQuery = loanApi
        .query()
        .withState(["ITEM_ON_LOAN"])
        .sortByNewest()
        .withSize(1000);

      const loansQueryString = loansQuery.qs();
      const loansResponse = await loanApi.list(loansQueryString);

      const activeFinesResponse = await fineApi.getActiveFines();
      const activeFines = activeFinesResponse.data.hits.filter(
        (fine) =>
          fine.status !== "resolved" &&
          fine.status !== "cancelled" &&
          fine.status !== "waived"
      );

      const unreturnedMaterials = this.processUnreturnedData(
        loansResponse.data.hits || [],
        activeFines
      );

      this.setState({ unreturnedMaterials, loading: false });

      if (this.props.onDataChange) {
        this.props.onDataChange(unreturnedMaterials);
      }
    } catch (error) {
      this.setState({
        error: `Failed to fetch unreturned materials: ${error.message}`,
        unreturnedMaterials: [],
        loading: false,
      });
    }
  };

  processUnreturnedData = (loans, activeFines) => {
    const patronMaterialsMap = new Map();

    const finesByLoanId = new Map();
    const finesByPatron = new Map();

    activeFines.forEach((fine) => {
      if (fine.loan_id) {
        finesByLoanId.set(fine.loan_id, fine);
      }

      const patronKey = fine.email || fine.name || "Unknown Patron";
      if (!finesByPatron.has(patronKey)) {
        finesByPatron.set(patronKey, []);
      }
      finesByPatron.get(patronKey).push(fine);
    });

    loans.forEach((loan) => {
      const patronKey =
        loan.metadata.patron?.email ||
        loan.metadata.patron?.name ||
        "Unknown Patron";

      if (!patronMaterialsMap.has(patronKey)) {
        patronMaterialsMap.set(patronKey, {
          patronName: loan.metadata.patron?.name || "Unknown Patron",
          patronEmail: loan.metadata.patron?.email || "No email",
          patronPhone: loan.metadata.patron?.phone || "No phone",
          patronId: loan.metadata.patron?.pid,
          unreturnedBooks: [],
          totalFineAmount: 0,
        });
      }

      const patronRecord = patronMaterialsMap.get(patronKey);
      const dueDate = DateTime.fromISO(loan.metadata.end_date);
      const today = DateTime.now();
      const calendarOverdueDays = Math.max(0, today.diff(dueDate, "days").days);
      const associatedFine = finesByLoanId.get(loan.metadata.pid);
      const actualFineAmount = associatedFine
        ? parseFloat(associatedFine.total_fine || 0)
        : 0;

      // Use centralized business days calculation
      const businessOverdueDays =
        actualFineAmount > 0 ? calculateBusinessDaysLuxon(dueDate, today) : 0;

      patronRecord.unreturnedBooks.push({
        title: loan.metadata.document?.title || "Unknown Title",
        author:
          loan.metadata.document?.authors?.[0]?.full_name || "Unknown Author",
        isbn: loan.metadata.document?.isbn || "N/A",
        callNumber: loan.metadata.item?.call_number || "N/A",
        itemBarcode: loan.metadata.item?.barcode || "N/A",
        dueDate: loan.metadata.end_date,
        overdueDays: businessOverdueDays,
        fineAmount: actualFineAmount,
        loanId: loan.metadata.pid,
        isOverdue: calendarOverdueDays > 0,
      });
    });

    Array.from(patronMaterialsMap.keys()).forEach((patronKey) => {
      const patronRecord = patronMaterialsMap.get(patronKey);
      const patronFines = finesByPatron.get(patronKey) || [];

      patronRecord.totalFineAmount = patronFines.reduce((total, fine) => {
        return total + parseFloat(fine.total_fine || 0);
      }, 0);
    });

    return Array.from(patronMaterialsMap.values());
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED);
  };

  renderUnreturnedMaterialsTable() {
    const { unreturnedMaterials } = this.state;

    if (!unreturnedMaterials || unreturnedMaterials.length === 0) {
      return (
        <Message info>
          <Message.Header>No Unreturned Materials Found</Message.Header>
          <p>There are currently no unreturned materials in the system.</p>
        </Message>
      );
    }

    return (
      <Table
        celled
        striped
        style={{
          fontSize: "14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Table.Header>
          <Table.Row style={{ backgroundColor: "#f8f9fa" }}>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Patron Information
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Unreturned Books
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Days Overdue
            </Table.HeaderCell>
            <Table.HeaderCell
              style={{
                fontWeight: "600",
                color: "#2c3e50",
                padding: "16px",
                borderBottom: "2px solid #dee2e6",
                textAlign: "center",
              }}
            >
              Fine Amount
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {unreturnedMaterials.map((patron, index) => (
            <React.Fragment key={index}>
              <Table.Row
                style={{
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                }}
              >
                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#2c3e50",
                        marginBottom: "8px",
                      }}
                    >
                      {patron.patronName}
                    </div>
                    {patron.patronEmail &&
                      patron.patronEmail !== "No email" && (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#6c757d",
                            marginTop: "4px",
                          }}
                        >
                          {patron.patronEmail}
                        </div>
                      )}
                  </div>
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  {patron.unreturnedBooks.map((book, bookIndex) => (
                    <div
                      key={bookIndex}
                      style={{
                        marginBottom:
                          bookIndex < patron.unreturnedBooks.length - 1
                            ? "16px"
                            : "0",
                        padding: "12px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        borderLeft: "4px solid #dc3545",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#2c3e50",
                          marginBottom: "6px",
                          lineHeight: "1.4",
                        }}
                      >
                        {book.title}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        {book.isbn && book.isbn !== "N/A" && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              backgroundColor: "#ffffff",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            ISBN: {book.isbn}
                          </span>
                        )}
                        {book.callNumber && book.callNumber !== "N/A" && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              backgroundColor: "#ffffff",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            Call #: {book.callNumber}
                          </span>
                        )}
                        {book.itemBarcode && book.itemBarcode !== "N/A" && (
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#6c757d",
                              backgroundColor: "#ffffff",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              border: "1px solid #dee2e6",
                            }}
                          >
                            Barcode: {book.itemBarcode}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "top",
                    textAlign: "center",
                    borderRight: "1px solid #dee2e6",
                  }}
                >
                  {patron.unreturnedBooks.map((book, bookIndex) => (
                    <div
                      key={bookIndex}
                      style={{
                        marginBottom:
                          bookIndex < patron.unreturnedBooks.length - 1
                            ? "16px"
                            : "0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "60px",
                      }}
                    >
                      {book.isOverdue ? (
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#000000",
                            textAlign: "center",
                          }}
                        >
                          {book.overdueDays} days overdue
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#000000",
                            textAlign: "center",
                          }}
                        >
                          Not overdue
                        </div>
                      )}
                    </div>
                  ))}
                </Table.Cell>

                <Table.Cell
                  style={{
                    padding: "20px",
                    verticalAlign: "middle",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Label
                      size="large"
                      color={patron.totalFineAmount > 0 ? "red" : "green"}
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        padding: "12px 16px",
                      }}
                    >
                      {this.formatCurrency(patron.totalFineAmount)}
                    </Label>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        backgroundColor: "#f8f9fa",
                        padding: "6px 10px",
                        borderRadius: "4px",
                        textAlign: "center",
                      }}
                    >
                      Total overdue:{" "}
                      {patron.unreturnedBooks.reduce(
                        (sum, book) => sum + book.overdueDays,
                        0
                      )}{" "}
                      days
                    </div>
                  </div>
                </Table.Cell>
              </Table.Row>

              {index < unreturnedMaterials.length - 1 && (
                <Table.Row>
                  <Table.Cell
                    colSpan="4"
                    style={{
                      padding: "0",
                      height: "12px",
                      backgroundColor: "#e9ecef",
                      borderTop: "2px solid #dee2e6",
                      borderBottom: "2px solid #dee2e6",
                    }}
                  />
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table>
    );
  }

  render() {
    const { loading, error } = this.state;

    if (error) {
      return (
        <Segment>
          <Message negative>
            <Message.Header>Something went wrong</Message.Header>
            <p>{error}</p>
          </Message>
        </Segment>
      );
    }

    return (
      <Segment loading={loading}>
        <Header as="h3" dividing>
          <Icon name="clipboard list" color="green" />
          <Header.Content>
            Unreturned Materials & Overdue Fines Report
            <Header.Subheader style={{ fontSize: "0.7em", color: "#666" }}>
              Comprehensive view of patrons with unreturned materials and
              calculated overdue fines.
            </Header.Subheader>
          </Header.Content>
        </Header>
        {this.renderUnreturnedMaterialsTable()}
      </Segment>
    );
  }
}

UnreturnedMaterialsTab.propTypes = {
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  onDataChange: PropTypes.func,
};

UnreturnedMaterialsTab.defaultProps = {
  fromDate: "",
  toDate: "",
  onDataChange: null,
};

export default UnreturnedMaterialsTab;
